/**
 * Browser-side First-Touch / Last-Touch Attribution Capture.
 *
 * Captures UTMs + click-IDs from the current URL and persists them in
 * localStorage so every form submission can attach a complete attribution
 * snapshot — no visible UI changes, no required fields, no impact on
 * submission rates.
 *
 * Storage model (localStorage):
 *   funstay_attribution_first_touch  — written ONCE on the very first
 *                                       visit with any attribution data.
 *                                       Never overwritten thereafter.
 *   funstay_attribution_last_touch   — written/refreshed every time a
 *                                       new attribution signal is seen
 *                                       (utm, gclid, fbclid, etc.).
 *
 * Public API:
 *   initAttributionCapture()    — call once on app mount.
 *   getAttributionSnapshot()    — returns the merged payload to attach
 *                                  to any form submission.
 *
 * Pure additive — never throws, never blocks rendering.
 */

const FIRST_KEY = 'funstay_attribution_first_touch';
const LAST_KEY = 'funstay_attribution_last_touch';

const TRACKED_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'fbclid', 'gclid', 'wbraid', 'gbraid',
];

function _safeStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

function _readJSON(key) {
  const ls = _safeStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function _writeJSON(key, value) {
  const ls = _safeStorage();
  if (!ls) return;
  try {
    ls.setItem(key, JSON.stringify(value));
  } catch (_) {
    /* quota / private mode — silent */
  }
}

function _readUrlAttribution() {
  if (typeof window === 'undefined' || !window.location) return {};
  try {
    const params = new URLSearchParams(window.location.search || '');
    const out = {};
    TRACKED_PARAMS.forEach((k) => {
      const v = params.get(k);
      if (v != null && v !== '') out[k] = v;
    });
    return out;
  } catch (_) {
    return {};
  }
}

function _readRef() {
  if (typeof document === 'undefined') return '';
  try { return document.referrer || ''; } catch (_) { return ''; }
}

function _readLandingUrl() {
  if (typeof window === 'undefined' || !window.location) return '';
  try { return window.location.href || ''; } catch (_) { return ''; }
}

function _deriveSource(attr) {
  if (attr.utm_source) return attr.utm_source;
  if (attr.gclid || attr.wbraid || attr.gbraid) return 'google';
  if (attr.fbclid) return 'facebook';
  return '';
}

function _deriveMedium(attr) {
  if (attr.utm_medium) return attr.utm_medium;
  if (attr.gclid || attr.fbclid || attr.wbraid || attr.gbraid) return 'cpc';
  return '';
}

function _deriveCampaign(attr) {
  return attr.utm_campaign || '';
}

/**
 * Initialize attribution tracking. Idempotent — safe to call multiple
 * times (e.g. on every route change). Captures first-touch ONCE; updates
 * last-touch whenever new attribution signals are present in the URL.
 */
export function initAttributionCapture() {
  try {
    const urlAttr = _readUrlAttribution();
    const hasUrlSignal = Object.keys(urlAttr).length > 0;
    const referrer = _readRef();
    const landingUrl = _readLandingUrl();
    const nowIso = new Date().toISOString();

    // ----- First touch (write only if not already written AND there
    // is something meaningful to capture — either a tracked param or a
    // referrer URL). Once written, never overwrite.
    const existingFirst = _readJSON(FIRST_KEY);
    if (!existingFirst && (hasUrlSignal || referrer)) {
      const firstTouch = {
        ...urlAttr,
        landing_page_url: landingUrl,
        referrer_url: referrer,
        first_touch_source: _deriveSource(urlAttr) || (referrer ? 'referral' : 'direct'),
        first_touch_medium: _deriveMedium(urlAttr) || (referrer ? 'referral' : 'direct'),
        first_touch_campaign: _deriveCampaign(urlAttr),
        first_touch_date: nowIso,
      };
      _writeJSON(FIRST_KEY, firstTouch);
    }

    // ----- Last touch (refresh when a new URL signal is detected).
    // If no URL signal but referrer exists and last_touch was never
    // written, seed it with referrer info so the first inbound visit
    // is recorded.
    const existingLast = _readJSON(LAST_KEY);
    if (hasUrlSignal) {
      const lastTouch = {
        ...urlAttr,
        landing_page_url: landingUrl,
        referrer_url: referrer,
        last_touch_source: _deriveSource(urlAttr),
        last_touch_medium: _deriveMedium(urlAttr),
        last_touch_campaign: _deriveCampaign(urlAttr),
        last_touch_date: nowIso,
      };
      _writeJSON(LAST_KEY, lastTouch);
    } else if (!existingLast && referrer) {
      _writeJSON(LAST_KEY, {
        landing_page_url: landingUrl,
        referrer_url: referrer,
        last_touch_source: 'referral',
        last_touch_medium: 'referral',
        last_touch_campaign: '',
        last_touch_date: nowIso,
      });
    }
  } catch (_) {
    /* never break the page */
  }
}

/**
 * Returns a flat attribution snapshot suitable for inclusion in any
 * form payload sent to /api/integrations/leads or /api/crm/opportunities.
 *
 * The returned shape is intentionally flat (keys are picked up directly
 * by lead-ingestion mappings AND surfaced into opportunity.marketing_attribution
 * via the connections.js `marketing_attribution` field below).
 *
 * Safe defaults: all values default to '' so attaching this snapshot
 * never introduces undefined/null pollution.
 */
export function getAttributionSnapshot() {
  const first = _readJSON(FIRST_KEY) || {};
  const last = _readJSON(LAST_KEY) || first || {};
  const currentUrl = _readLandingUrl();

  // Lead-touch = the touch responsible for THIS lead submission.
  // Prefer the most recent (last) touch; fall back to first.
  const lead_source = last.last_touch_source || first.first_touch_source || '';
  const lead_medium = last.last_touch_medium || first.first_touch_medium || '';
  const lead_campaign = last.last_touch_campaign || first.first_touch_campaign || '';

  return {
    // Touch snapshot
    first_touch_source: first.first_touch_source || '',
    first_touch_medium: first.first_touch_medium || '',
    first_touch_campaign: first.first_touch_campaign || '',
    first_touch_date: first.first_touch_date || '',
    last_touch_source: last.last_touch_source || '',
    last_touch_medium: last.last_touch_medium || '',
    last_touch_campaign: last.last_touch_campaign || '',
    last_touch_date: last.last_touch_date || '',
    lead_source,
    lead_medium,
    lead_campaign,
    // UTM + click-IDs (prefer last-touch values; if missing, fall back
    // to first-touch — this is exactly the data-flow analysts expect).
    utm_source: last.utm_source || first.utm_source || '',
    utm_medium: last.utm_medium || first.utm_medium || '',
    utm_campaign: last.utm_campaign || first.utm_campaign || '',
    utm_term: last.utm_term || first.utm_term || '',
    utm_content: last.utm_content || first.utm_content || '',
    fbclid: last.fbclid || first.fbclid || '',
    gclid: last.gclid || first.gclid || '',
    wbraid: last.wbraid || first.wbraid || '',
    gbraid: last.gbraid || first.gbraid || '',
    // URLs
    landing_page_url: first.landing_page_url || '',
    page_url: currentUrl,
    referrer_url: last.referrer_url || first.referrer_url || '',
  };
}

/**
 * Test-only / ops-only helper to clear stored attribution. Not exposed
 * in any UI; intentionally undocumented for end users.
 */
export function _resetAttributionForTests() {
  const ls = _safeStorage();
  if (!ls) return;
  try { ls.removeItem(FIRST_KEY); ls.removeItem(LAST_KEY); } catch (_) { /* ignore */ }
}
